import { z } from 'zod';
import { ActorRefSchema } from './audit.js';
import { innSchema, isValidInnIp, isValidInnOoo } from './inn.js';
import { LegalEntityRequisitesSchema } from './legal-entity-requisites.js';
import { nonEmptyPatch } from './_patch.js';
import { phoneSchema } from './phone.js';
import { requiredClientText } from './clients-legal-input.js';

// Field bounds — exported so the web forms can render input limits without
// duplicating literals.
export const CLIENT_NAME_MAX_LENGTH = 200;
export const CLIENT_COMMENT_MAX_LENGTH = 2000;
export const CLIENT_CONTACT_FULL_NAME_MAX_LENGTH = 200;
export const CLIENT_CONTACT_COMMENT_MAX_LENGTH = 2000;
export const CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH = 200;
export const CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH = 2000;

/**
 * @aiPurpose closed enum of legal-entity forms nested under a Client. The
 *   form determines INN length (OOO = 10 digits, IP = 12 digits) and is
 *   immutable post-create — changing form would invalidate the INN slot
 *   uniqueness contract. Wire layer rejects any other value.
 */
export enum ClientLegalEntityType {
  OOO = 'OOO',
  IP = 'IP',
}

export const ClientLegalEntityTypeSchema = z.nativeEnum(ClientLegalEntityType);

/**
 * @aiPurpose sticky, automatic CRM lifecycle of a Client (#1830). `POTENTIAL`
 *   on create; flips to `WORKING` the first time a Deal is created for the
 *   client and never reverts. There is NO inbound write for this field — it is
 *   derived from deals and read-only on the wire.
 * @aiInvariant forward-only: `POTENTIAL` → `WORKING` only. Mirrored by the
 *   Prisma `ClientLifecycle` enum and checked by `scripts/check-enum-sync.sh`.
 */
export enum ClientLifecycle {
  POTENTIAL = 'POTENTIAL',
  WORKING = 'WORKING',
}

export const ClientLifecycleSchema = z.nativeEnum(ClientLifecycle);

/**
 * @aiPurpose closed enum of business-state mutations on the Client aggregate.
 *   Every aggregate write emits exactly one `ClientVersion` row with one of
 *   these `action` values; the catalog is append-only because version rows
 *   reference these literals forever. `CLIENT_OWNER_REASSIGN` is intentionally
 *   absent in this issue (#601) — owner reassignment is out of scope.
 * @aiInvariant mirrored by the Prisma `ClientVersionAction` enum (added in
 *   PR2) and checked by `scripts/check-enum-sync.sh`.
 */
export enum ClientVersionAction {
  CLIENT_CREATE = 'CLIENT_CREATE',
  CLIENT_UPDATE = 'CLIENT_UPDATE',
  CLIENT_ARCHIVE = 'CLIENT_ARCHIVE',
  CLIENT_RESTORE = 'CLIENT_RESTORE',
  CLIENT_VERSION_RESTORE = 'CLIENT_VERSION_RESTORE',
  CLIENT_CONTACT_ADD = 'CLIENT_CONTACT_ADD',
  CLIENT_CONTACT_UPDATE = 'CLIENT_CONTACT_UPDATE',
  CLIENT_CONTACT_ARCHIVE = 'CLIENT_CONTACT_ARCHIVE',
  CLIENT_CONTACT_RESTORE = 'CLIENT_CONTACT_RESTORE',
  CLIENT_CONTACT_SET_PRIMARY = 'CLIENT_CONTACT_SET_PRIMARY',
  CLIENT_LEGAL_ADD = 'CLIENT_LEGAL_ADD',
  CLIENT_LEGAL_UPDATE = 'CLIENT_LEGAL_UPDATE',
  CLIENT_LEGAL_ARCHIVE = 'CLIENT_LEGAL_ARCHIVE',
  CLIENT_LEGAL_RESTORE = 'CLIENT_LEGAL_RESTORE',
  // #2246 (D-018): the derived `Client.lifecycle POTENTIAL→WORKING` flip at the
  // first CLOSING→DEAL conversion now runs THROUGH the aggregate contract
  // (lock+version+audit), superseding the #1830 raw-SQL bypass. This action
  // labels that version row.
  CLIENT_LIFECYCLE_WORKING = 'CLIENT_LIFECYCLE_WORKING',
}

export const ClientVersionActionSchema = z.nativeEnum(ClientVersionAction);

// Shared refinement: a legal entity's `inn` length and checksum must match
// its declared `type`. OOO carries a 10-digit INN, IP a 12-digit INN. The
// refinement is applied to every legal-entity-shaped schema — input, row,
// and snapshot — so the wire layer rejects every impossible state, not
// only the create path. Without this, an `OOO`-typed row carrying a
// 12-digit IP-checksum INN would claim the IP slot in the global INN
// uniqueness contract.
function legalEntityTypeBoundInn(
  v: { type: ClientLegalEntityType; inn: string },
  ctx: z.RefinementCtx,
): void {
  if (v.type === ClientLegalEntityType.OOO && !isValidInnOoo(v.inn)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['inn'],
      message: 'OOO INN must be 10 digits with valid checksum',
    });
  }
  if (v.type === ClientLegalEntityType.IP && !isValidInnIp(v.inn)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['inn'],
      message: 'IP INN must be 12 digits with valid checksum',
    });
  }
}

// Shared refinement: the biconditional between `action` and `restoredFrom*`
// on every `ClientVersion`-shaped schema. `CLIENT_VERSION_RESTORE` rows
// must carry both `restoredFromVersionId` and `restoredFromVersionNumber`;
// every other action keeps them null. Encoded as a refinement so list and
// detail share the invariant without diverging.
function clientVersionRestoreInvariant(
  v: {
    action: ClientVersionAction;
    restoredFromVersionId: string | null;
    restoredFromVersionNumber: number | null;
  },
  ctx: z.RefinementCtx,
): void {
  const isRestore = v.action === ClientVersionAction.CLIENT_VERSION_RESTORE;
  const hasRestoredFrom = v.restoredFromVersionId !== null || v.restoredFromVersionNumber !== null;
  const hasBothRestoredFrom =
    v.restoredFromVersionId !== null && v.restoredFromVersionNumber !== null;
  if (isRestore && !hasBothRestoredFrom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['restoredFromVersionId'],
      message:
        'CLIENT_VERSION_RESTORE requires both restoredFromVersionId and restoredFromVersionNumber',
    });
  }
  if (!isRestore && hasRestoredFrom) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['restoredFromVersionId'],
      message:
        'restoredFromVersionId / restoredFromVersionNumber must be null for non-restore actions',
    });
  }
}

/**
 * @aiPurpose closed selector for the Client list `archived` filter. The web
 *   list uses this to switch between active-only (default), archived-only,
 *   and the full ledger view.
 */
export const ClientArchiveFilterSchema = z.enum(['active', 'archived', 'all']);
export type ClientArchiveFilter = z.infer<typeof ClientArchiveFilterSchema>;

/**
 * @aiPurpose denormalised manager pointer returned on Client list/detail.
 *   UI renders `name` for display; admin filter submits `id` so the wire
 *   contract carries a stable UUID even though the business handle is the
 *   display label. Distinct from `ActorRefSchema` (which carries email and
 *   is reused for audit) to keep this surface minimal — managerName has no
 *   email exposure.
 */
export const ClientManagerRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type ClientManagerRef = z.infer<typeof ClientManagerRefSchema>;

// ─── ClientContact ──────────────────────────────────────────────────────────

/**
 * @aiPurpose wire shape for one ClientContact row returned on detail
 *   responses. `phone` is canonical E.164 (validated by `phoneSchema`).
 *   `isPrimary` may be true at most once per active client; the business
 *   invariant is enforced server-side under the per-client advisory lock,
 *   not at this schema.
 * @aiPii `fullName`, `phone`, `comment` are PII — redact in logs.
 */
export const ClientContactSchema = z
  .object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    fullName: z.string().min(1).max(CLIENT_CONTACT_FULL_NAME_MAX_LENGTH),
    phone: phoneSchema,
    comment: z.string().max(CLIENT_CONTACT_COMMENT_MAX_LENGTH).nullable(),
    isPrimary: z.boolean(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type ClientContact = z.infer<typeof ClientContactSchema>;

/**
 * @aiPurpose input for `POST /api/clients/:id/contacts` and (as a sub-object)
 *   for `POST /api/clients` first-contact creation. `isPrimary` is NOT on the
 *   input — primacy is owned by the dedicated set-primary endpoint, except
 *   for the first contact created together with the client, which the
 *   service marks primary in the same transaction.
 */
export const ClientContactInputSchema = z
  .object({
    fullName: z.string().min(1).max(CLIENT_CONTACT_FULL_NAME_MAX_LENGTH),
    phone: phoneSchema,
    // `comment` is product-optional: callers may omit the field entirely on
    // create. Explicit `null` is also accepted so a parent form that always
    // serialises the field clears the slot symmetrically. PATCH inherits
    // optionality via `.partial()` and additionally uses `null` to
    // explicitly clear an existing comment.
    comment: z.string().max(CLIENT_CONTACT_COMMENT_MAX_LENGTH).nullable().optional(),
  })
  .strict();
export type ClientContactInput = z.infer<typeof ClientContactInputSchema>;

export const PatchClientContactInputSchema = nonEmptyPatch(
  ClientContactInputSchema.partial().strict(),
);
export type PatchClientContactInput = z.infer<typeof PatchClientContactInputSchema>;

// ─── ClientLegalEntity ──────────────────────────────────────────────────────

/**
 * @aiPurpose wire shape for one ClientLegalEntity row. `type` is immutable
 *   post-create; `inn` checksum is validated by `innSchema`, and the
 *   type-bound length (OOO = 10 digits, IP = 12 digits) is enforced at the
 *   create-input level via `.superRefine`. PATCH inn validation falls back
 *   to the universal checksum check, with the service revalidating against
 *   the entity's current type.
 * @aiPii `name`, `inn`, `requisites` are PII / financial requisites — redact
 *   in logs.
 */
export const ClientLegalEntitySchema = z
  .object({
    id: z.string().uuid(),
    clientId: z.string().uuid(),
    type: ClientLegalEntityTypeSchema,
    name: z.string().min(1).max(CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH),
    inn: innSchema,
    // Legacy flat requisites — DERIVED (#2242 FR-2) from the canonical (first
    // active) `requisitesSets` row's `details`. Kept on the wire so existing
    // readers keep working while storage moved to `LegalEntityRequisites`.
    requisites: z.string().min(1).max(CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH),
    // Structured 1..n bank-requisites sets (#2242 FR-2). Optional for
    // rolling-deploy tolerance — a new bundle must still parse an old backend's
    // response that predates the field under `.strict()`; the backend always
    // emits it post-deploy (active + archived rows, like `ClientContact`).
    requisitesSets: z.array(LegalEntityRequisitesSchema).optional(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine(legalEntityTypeBoundInn);
export type ClientLegalEntity = z.infer<typeof ClientLegalEntitySchema>;

const ClientLegalEntityCreateInnerSchema = z
  .object({
    type: ClientLegalEntityTypeSchema,
    name: requiredClientText(CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH),
    inn: innSchema,
    requisites: requiredClientText(CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH),
  })
  .strict();

/**
 * @aiPurpose input for `POST /api/clients/:id/legal-entities`. `.superRefine`
 *   binds INN length to the declared `type`: an `OOO` payload must carry a
 *   10-digit INN, an `IP` payload a 12-digit INN. Without this guard the
 *   global INN uniqueness slot could be claimed by an `IP` row carrying an
 *   `OOO`-shaped INN.
 */
export const ClientLegalEntityInputSchema =
  ClientLegalEntityCreateInnerSchema.superRefine(legalEntityTypeBoundInn);
export type ClientLegalEntityInput = z.infer<typeof ClientLegalEntityInputSchema>;

// PATCH excludes `type` (immutable) and uses the inner object directly so
// `.partial()` works; the service re-checks `inn` against the entity's
// current type after the schema accepts the payload.
const ClientLegalEntityPatchInnerSchema = ClientLegalEntityCreateInnerSchema.omit({ type: true });

export const PatchClientLegalEntityInputSchema = nonEmptyPatch(
  ClientLegalEntityPatchInnerSchema.partial().strict(),
);
export type PatchClientLegalEntityInput = z.infer<typeof PatchClientLegalEntityInputSchema>;

// ─── Client list / detail ───────────────────────────────────────────────────

/**
 * @aiPurpose primary-contact summary embedded in `ClientListItem`. Equals
 *   the active primary contact of an active client; equals `null` for an
 *   archived client whose archived state predates the always-one-primary
 *   invariant or whose primary was archived separately (defensive). UI
 *   renders the contact's `fullName` + `phone` on the row.
 * @aiPii fullName + phone are PII — redact in logs.
 */
export const ClientListItemPrimaryContactSchema = z
  .object({
    id: z.string().uuid(),
    fullName: z.string().min(1).max(CLIENT_CONTACT_FULL_NAME_MAX_LENGTH),
    phone: phoneSchema,
  })
  .strict();
export type ClientListItemPrimaryContact = z.infer<typeof ClientListItemPrimaryContactSchema>;

/**
 * @aiPurpose one row in the cursor-paginated `GET /api/clients` list.
 *   `legalEntitiesCount` counts active legal entities only (archived rows
 *   are invisible to the list summary, consistent with active-only search).
 *   `manager` carries UUID + display so the admin filter UI can render
 *   names without a separate users lookup.
 */
export const ClientListItemSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(CLIENT_NAME_MAX_LENGTH),
    comment: z.string().max(CLIENT_COMMENT_MAX_LENGTH).nullable(),
    archivedAt: z.string().datetime().nullable(),
    manager: ClientManagerRefSchema,
    primaryContact: ClientListItemPrimaryContactSchema.nullable(),
    legalEntitiesCount: z.number().int().min(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type ClientListItem = z.infer<typeof ClientListItemSchema>;

/**
 * @aiPurpose full Client aggregate returned by `GET /api/clients/:id`.
 *   `contacts` and `legalEntities` include both active and archived rows
 *   so the detail page can render the archive section without a second
 *   request; the page filters client-side by `archivedAt IS NULL`.
 */
export const ClientDetailSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(CLIENT_NAME_MAX_LENGTH),
    comment: z.string().max(CLIENT_COMMENT_MAX_LENGTH).nullable(),
    archivedAt: z.string().datetime().nullable(),
    // #1830: sticky derived lifecycle. Read-only — no patch/create field
    // mirrors it; the deal-create transaction is the only writer.
    // WHY (#1830): optional for rolling-deploy tolerance — a new bundle must
    //   still parse an old backend's response that predates this field under
    //   .strict(); the backend always emits it post-deploy.
    lifecycle: ClientLifecycleSchema.optional(),
    manager: ClientManagerRefSchema,
    contacts: z.array(ClientContactSchema),
    legalEntities: z.array(ClientLegalEntitySchema),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type ClientDetail = z.infer<typeof ClientDetailSchema>;

// ─── Create / patch Client ──────────────────────────────────────────────────

/**
 * @aiPurpose input for `POST /api/clients`. The create flow is atomic: the
 *   client row and its first primary contact are written together inside a
 *   single backend transaction. A first contact is required because the
 *   aggregate invariant demands at least one active contact at all times.
 */
export const CreateClientInputSchema = z
  .object({
    name: z.string().min(1).max(CLIENT_NAME_MAX_LENGTH),
    // `comment` is product-optional on create. Callers may omit the field
    // entirely; explicit `null` is also accepted so forms that always
    // serialise the field clear the slot symmetrically.
    comment: z.string().max(CLIENT_COMMENT_MAX_LENGTH).nullable().optional(),
    firstContact: ClientContactInputSchema,
  })
  .strict();
export type CreateClientInput = z.infer<typeof CreateClientInputSchema>;

const ClientPatchInnerSchema = z
  .object({
    name: z.string().min(1).max(CLIENT_NAME_MAX_LENGTH),
    comment: z.string().max(CLIENT_COMMENT_MAX_LENGTH).nullable(),
  })
  .strict();

export const PatchClientInputSchema = nonEmptyPatch(ClientPatchInnerSchema.partial().strict());
export type PatchClientInput = z.infer<typeof PatchClientInputSchema>;

// ─── List query / response ──────────────────────────────────────────────────

/**
 * @aiPurpose query string for `GET /api/clients` — cursor-paginated list
 *   with search, archive selector, and admin-only manager filter.
 * @aiInvariant `.strict()` blocks unknown keys. `archived` defaults to
 *   `'active'` so the picker omits archived rows by default. `managerId`
 *   is honored only for Admin callers; the backend silently ignores it for
 *   non-Admin and applies ownership scope (#601 Resolved Decision). `limit`
 *   clamped to 1..100 with default 50. `cursor` is opaque base64url(`{
 *   updatedAt, id}`) decoded server-side; tampering yields 400.
 */
export const GetClientsQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    archived: ClientArchiveFilterSchema.default('active'),
    managerId: z.string().uuid().optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetClientsQuery = z.infer<typeof GetClientsQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/clients`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying query
 *   returned ≤ `limit` rows; otherwise it carries the encoded
 *   `(updatedAt, id)` of the last returned row.
 */
export const GetClientsResponseSchema = z
  .object({
    items: z.array(ClientListItemSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetClientsResponse = z.infer<typeof GetClientsResponseSchema>;

// ─── Version snapshot / list / restore ──────────────────────────────────────

/**
 * @aiPurpose contact row inside a `ClientVersion.snapshot`. Carries the
 *   stable UUID so version-restore can update the existing row (preserving
 *   FK relations such as audit pointers) rather than recreating it with a
 *   fresh id. Timestamps are intentionally absent — they belong to the
 *   live row, not the historical snapshot.
 * @aiPii fullName, phone, comment are PII — `ClientVersion.snapshot` is the
 *   only sanctioned home for PII-bearing client state outside the live
 *   aggregate.
 */
export const ClientVersionContactSnapshotSchema = z
  .object({
    id: z.string().uuid(),
    fullName: z.string().min(1).max(CLIENT_CONTACT_FULL_NAME_MAX_LENGTH),
    phone: phoneSchema,
    comment: z.string().max(CLIENT_CONTACT_COMMENT_MAX_LENGTH).nullable(),
    isPrimary: z.boolean(),
    archivedAt: z.string().datetime().nullable(),
  })
  .strict();
export type ClientVersionContactSnapshot = z.infer<typeof ClientVersionContactSnapshotSchema>;

/**
 * @aiPurpose legal-entity row inside a `ClientVersion.snapshot`. Same row-
 *   identity contract as the contact snapshot. `type` is preserved verbatim
 *   so restore can refuse a snapshot whose declared type no longer matches
 *   the live row's immutable type (defensive — should not happen).
 */
export const ClientVersionLegalEntitySnapshotSchema = z
  .object({
    id: z.string().uuid(),
    type: ClientLegalEntityTypeSchema,
    name: z.string().min(1).max(CLIENT_LEGAL_ENTITY_NAME_MAX_LENGTH),
    inn: innSchema,
    requisites: z.string().min(1).max(CLIENT_LEGAL_ENTITY_REQUISITES_MAX_LENGTH),
    archivedAt: z.string().datetime().nullable(),
  })
  .strict()
  .superRefine(legalEntityTypeBoundInn);
export type ClientVersionLegalEntitySnapshot = z.infer<
  typeof ClientVersionLegalEntitySnapshotSchema
>;

/**
 * @aiPurpose full aggregate snapshot stored on `ClientVersion.snapshot` and
 *   returned by `GET /api/clients/:id/versions/:versionId`. Captures every
 *   business-state field needed to deterministically restore the aggregate.
 *   `managerId` is preserved for historical/provenance display only —
 *   `CLIENT_VERSION_RESTORE` keeps the live aggregate's current owner.
 * @aiInvariant the live aggregate's row identity (client UUID + contact /
 *   legal-entity UUIDs) is byte-stable across versions; restore matches
 *   rows by id, archives rows present-now-but-absent-in-snapshot, and
 *   updates rows present-in-both.
 * @aiPii contains the full PII surface of the client aggregate. Audit
 *   payloads must NOT duplicate this content; only `ClientVersion.snapshot`
 *   is the sanctioned PII-bearing store.
 */
export const ClientVersionSnapshotSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1).max(CLIENT_NAME_MAX_LENGTH),
    comment: z.string().max(CLIENT_COMMENT_MAX_LENGTH).nullable(),
    managerId: z.string().uuid(),
    archivedAt: z.string().datetime().nullable(),
    contacts: z.array(ClientVersionContactSnapshotSchema),
    legalEntities: z.array(ClientVersionLegalEntitySnapshotSchema),
  })
  .strict();
export type ClientVersionSnapshot = z.infer<typeof ClientVersionSnapshotSchema>;

/**
 * @aiPurpose metadata-only row in `GET /api/clients/:id/versions`. Snapshots
 *   live separately under the detail endpoint so the history tab can load
 *   1000+ versions without pulling 1000 PII-bearing payloads. `restoredFrom*`
 *   is non-null only on `CLIENT_VERSION_RESTORE` rows and points back at the
 *   snapshot the operator chose to restore from.
 */
const ClientVersionListItemBaseSchema = z
  .object({
    id: z.string().uuid(),
    versionNumber: z.number().int().min(1),
    action: ClientVersionActionSchema,
    actor: ActorRefSchema,
    restoredFromVersionId: z.string().uuid().nullable(),
    restoredFromVersionNumber: z.number().int().min(1).nullable(),
    createdAt: z.string().datetime(),
  })
  .strict();

export const ClientVersionListItemSchema = ClientVersionListItemBaseSchema.superRefine(
  clientVersionRestoreInvariant,
);
export type ClientVersionListItem = z.infer<typeof ClientVersionListItemSchema>;

/**
 * @aiPurpose response for `GET /api/clients/:id/versions/:versionId`.
 *   `ClientVersionListItemSchema` fields + full `snapshot`. The snapshot
 *   alone is enough to render a complete read-only diff of the historical
 *   state against the live aggregate. The same restore-action ↔ restoredFrom
 *   biconditional applies as on the list item.
 */
export const ClientVersionDetailSchema = ClientVersionListItemBaseSchema.extend({
  snapshot: ClientVersionSnapshotSchema,
}).superRefine(clientVersionRestoreInvariant);
export type ClientVersionDetail = z.infer<typeof ClientVersionDetailSchema>;

/**
 * @aiPurpose query string for `GET /api/clients/:id/versions`.
 * @aiInvariant cursor is opaque base64url(`{createdAt, id}`) for the
 *   `(createdAt DESC, id DESC)` sort. `limit` clamped to 1..100, default 50.
 */
export const GetClientVersionsQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type GetClientVersionsQuery = z.infer<typeof GetClientVersionsQuerySchema>;

export const GetClientVersionsResponseSchema = z
  .object({
    items: z.array(ClientVersionListItemSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetClientVersionsResponse = z.infer<typeof GetClientVersionsResponseSchema>;

/**
 * @aiPurpose response for `POST /api/clients/:id/versions/:versionId/restore`.
 *   Returns the freshly-applied aggregate plus the new version row generated
 *   by the restore operation (action = `CLIENT_VERSION_RESTORE`). Callers
 *   replace cached aggregate state with `client` and append `version` to the
 *   history list without refetching either endpoint.
 */
export const RestoreClientVersionResponseSchema = z
  .object({
    client: ClientDetailSchema,
    version: ClientVersionListItemSchema,
  })
  .strict()
  .superRefine((v, ctx) => {
    // The version row returned by a successful restore must encode the
    // restore action itself — anything else means the service emitted the
    // wrong action code or wired a non-restore version into the response,
    // both of which would corrupt the history's restore-from chain.
    if (v.version.action !== ClientVersionAction.CLIENT_VERSION_RESTORE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['version', 'action'],
        message: 'restore response version must carry action=CLIENT_VERSION_RESTORE',
      });
    }
  });
export type RestoreClientVersionResponse = z.infer<typeof RestoreClientVersionResponseSchema>;

// ─── Domain error metadata ──────────────────────────────────────────────────

/**
 * @aiPurpose convention for the `ApiError.meta` payload emitted alongside
 *   `ErrorCode.CLIENT_ARCHIVED_READONLY` (HTTP 409). Surface is intentionally
 *   tiny — a single `clientId` — so the UI can show a "restore parent first"
 *   CTA without re-querying. PII is NOT included; callers already know the
 *   parent aggregate from the route they hit.
 */
export const ClientArchivedReadonlyMetaSchema = z
  .object({
    clientId: z.string().uuid(),
  })
  .strict();
export type ClientArchivedReadonlyMeta = z.infer<typeof ClientArchivedReadonlyMetaSchema>;
